import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../../ui/dropdown-menu';
import {
  EllipsisVerticalIcon,
  FolderOpenIcon,
  DocumentArrowDownIcon,
  CodeBracketIcon,
  ArchiveBoxIcon,
  ClockIcon,
  LinkIcon,
  XCircleIcon,
  CheckCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { OmbudsmanSimulation, OmbudsmanOutcome } from '../../../../types/workspace';
import { getProductIcon } from '../../../../lib/insurance-product-icons';

interface SimulationCardProps {
  simulation: OmbudsmanSimulation;
  onOpen?: (id: string) => void;
  onExportPDF?: (id: string) => void;
  onDownloadJSON?: (id: string) => void;
  onArchive?: (id: string) => void;
}

const outcomeConfig: Record<OmbudsmanOutcome, {
  bar: string;
  badge: string;
  icon: React.ReactNode;
  label: string;
}> = {
  upheld: {
    bar: 'bg-red-500',
    badge: 'bg-red-100 text-red-800 border-red-200',
    icon: <XCircleIcon className="w-3.5 h-3.5" />,
    label: 'Likely Upheld',
  },
  not_upheld: {
    bar: 'bg-green-500',
    badge: 'bg-green-100 text-green-800 border-green-200',
    icon: <CheckCircleIcon className="w-3.5 h-3.5" />,
    label: 'Likely Not Upheld',
  },
  unknown: {
    bar: 'bg-gray-300',
    badge: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: <QuestionMarkCircleIcon className="w-3.5 h-3.5" />,
    label: 'Unknown',
  },
};

const SimulationCard: React.FC<SimulationCardProps> = ({
  simulation,
  onOpen,
  onExportPDF,
  onDownloadJSON,
  onArchive,
}) => {
  const navigate = useNavigate();
  const productConfig = getProductIcon(simulation.productType);
  const ProductIcon = productConfig.icon;
  const outcome = outcomeConfig[simulation.predicted_outcome];

  const handleCardClick = () => {
    if (onOpen) {
      onOpen(simulation.id);
    } else {
      // Default navigation behavior
      navigate(`/v3/workspace/ombudsman/${simulation.id}`);
    }
  };

  const formatConfidence = (score: number): string => {
    return `${Math.round(score * 100)}%`;
  };

  const formatRelativeTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
      return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
    } catch {
      return 'recently';
    }
  };

  const borderColor = simulation.predicted_outcome === 'upheld'
    ? 'border-t-red-600'
    : simulation.predicted_outcome === 'not_upheld'
    ? 'border-t-green-600'
    : 'border-t-gray-400';

  return (
    <div
      className={`relative bg-white rounded-xl border border-gray-200 ${borderColor} border-t-2 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer`}
      onClick={handleCardClick}
    >
      {/* Actions Menu - Top Right */}
      <div className="absolute top-6 right-6 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="text-muted-foreground hover:text-foreground p-2 rounded-md hover:bg-gray-100 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <EllipsisVerticalIcon className="w-5 h-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                handleCardClick();
              }}
            >
              <FolderOpenIcon className="w-4 h-4 mr-2" />
              Open Simulation
            </DropdownMenuItem>
            {onExportPDF && (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  onExportPDF(simulation.id);
                }}
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                Export Guidance Pack (PDF)
              </DropdownMenuItem>
            )}
            {onDownloadJSON && (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  onDownloadJSON(simulation.id);
                }}
              >
                <CodeBracketIcon className="w-4 h-4 mr-2" />
                Download JSON
              </DropdownMenuItem>
            )}
            {onArchive && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    onArchive(simulation.id);
                  }}
                  className="text-destructive"
                >
                  <ArchiveBoxIcon className="w-4 h-4 mr-2" />
                  Archive
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Card Body */}
      <div className="p-6 pr-16 flex flex-col min-h-[220px]">
        {/* Radial Chart and Title Section */}
        <div className="flex items-start gap-4 mb-4">
          {/* Radial Confidence Visualization - Top Left */}
          {simulation.confidence_score && (
            <div className="relative flex-shrink-0">
              <svg width="56" height="56" viewBox="0 0 56 56" className="transform -rotate-90">
                {/* Background circle */}
                <circle
                  cx="28"
                  cy="28"
                  r="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="5"
                  className="text-gray-200"
                />
                {/* Progress circle */}
                <circle
                  cx="28"
                  cy="28"
                  r="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="5"
                  strokeDasharray={`${2 * Math.PI * 24}`}
                  strokeDashoffset={`${2 * Math.PI * 24 * (1 - simulation.confidence_score)}`}
                  strokeLinecap="round"
                  className={
                    simulation.predicted_outcome === 'upheld'
                      ? 'text-red-600'
                      : simulation.predicted_outcome === 'not_upheld'
                      ? 'text-green-600'
                      : 'text-gray-400'
                  }
                />
              </svg>
              {/* Centered percentage */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-bold text-foreground">
                  {formatConfidence(simulation.confidence_score)}
                </span>
              </div>
            </div>
          )}

          {/* Title */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground text-base leading-[1.5] line-clamp-2">
              {simulation.title || 'Untitled Simulation'}
            </h3>
          </div>
        </div>

        {/* Product Type */}
        <div className="flex items-center gap-2 text-sm mb-4">
          <div className={`p-1 rounded ${productConfig.bgColor}`}>
            <ProductIcon className={`w-4 h-4 ${productConfig.color}`} />
          </div>
          <span className="font-medium text-muted-foreground">{productConfig.label}</span>
        </div>

        {/* Spacer to push divider and metadata to bottom */}
        <div className="flex-1" />

        {/* Divider */}
        <div className="border-t border-gray-200 mb-3" />

        {/* Metadata Row - Stuck to bottom */}
        <div className="flex items-center text-xs text-muted-foreground">
          <ClockIcon className="w-3.5 h-3.5 mr-1" />
          <span>{formatRelativeTime(simulation.updated_at)}</span>
          {simulation.signal && (
            <>
              <span className="mx-2">•</span>
              <span>#{simulation.signal.reference_number}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimulationCard;
